// src/components/vcard/CategoryFields.tsx
import React, { useState } from "react";
import { AlertCircle } from "lucide-react";
import type { VCard, VCardCategory } from "../../types/vcard";
import HomeInfo from "./HomeInfo";
import LuggageInfo from "./LuggageInfo";
import TechInfo from "./TechInfo";
import ExtremeKitInfo from "./ExtremeKitInfo";
import BusinessInfo from "./BusinessInfo";
import { useVCardStore } from "../../store/vcardStore";
import { useAuthStore } from "../../store/authStore";

interface CategoryFieldsProps {
  category: VCardCategory;
  formData: Partial<VCard>;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  isSubmitting?: boolean;
  showSaveConfirmation?: boolean;
}

const CategoryFields: React.FC<CategoryFieldsProps> = ({
  category,
  formData,
  handleChange,
  handleSubmit,
  isSubmitting = false,
  showSaveConfirmation = false,
}) => {
  const [error, setError] = useState<string | null>(null);
  const user = useAuthStore((state) => state.user);
  const { toggleVCardWithAddress } = useVCardStore();

  const handleToggle = async () => {
    if (!user) return;

    // Get current state before toggle
    const currentVCard = formData;
    const newActiveState = !currentVCard.isActive;

    try {
      // Update local state immediately
      handleChange({
        target: {
          name: "isActive",
          value: newActiveState,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>);

      // Make the API call
      await toggleVCardWithAddress(category, user.id);
    } catch (error) {
      // Revert the optimistic update on error
      handleChange({
        target: {
          name: "isActive",
          value: !newActiveState,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>);

      setError(
        error instanceof Error ? error.message : "Failed to toggle category",
      );
    }
  };

  const renderCategoryFields = () => {
    const commonProps = {
      formData,
      handleChange,
      handleSubmit,
      isSubmitting,
      showSaveConfirmation,
      onToggle: handleToggle,
      isActive: formData.isActive || false,
    };

    switch (category) {
      case "home":
        return <HomeInfo {...commonProps} />;
      case "luggage":
        // LuggageInfo now handles toggle internally with useUniversalToggleWithAddress
        return (
          <LuggageInfo
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            showSaveConfirmation={showSaveConfirmation}
          />
        );
      case "myTech":
        // TechInfo now handles toggle internally with useUniversalToggleWithAddress
        return (
          <TechInfo
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            showSaveConfirmation={showSaveConfirmation}
            isActive={formData.isActive || false}
          />
        );
      case "extremeKit":
        return <ExtremeKitInfo {...commonProps} />;
      case "business":
        // BusinessInfo now handles toggle internally with useUniversalToggleWithAddress
        return (
          <BusinessInfo
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            showSaveConfirmation={showSaveConfirmation}
            isActive={formData.isActive || false}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-center space-x-2 p-4 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {renderCategoryFields()}
    </div>
  );
};

export default CategoryFields;
